import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles, MessageCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PLANNER_STEPS, waLink } from "@/data/site";
import { SectionHeading } from "@/components/pp/Shared";
import { submitPlanner, track } from "@/lib/api";

const CHIP = (active) =>
  `px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#f59e0b] text-black" : "bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10"}`;

const initial = {
  business_type: "", services: [], budget: "", objective: "", platforms: [],
  start_time: "", name: "", phone: "", email: "", details: "",
};

export default function AIPlanner() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (k, v) => setData((d) => ({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] }));

  const steps = [
    { key: "business_type", title: "What type of business do you operate?", type: "single", opts: PLANNER_STEPS.businessTypes },
    { key: "services", title: "Which services are you interested in?", type: "multi", opts: PLANNER_STEPS.services },
    { key: "budget", title: "What is your monthly marketing budget?", type: "single", opts: PLANNER_STEPS.budgets },
    { key: "objective", title: "What is your main objective?", type: "single", opts: PLANNER_STEPS.objectives },
    { key: "platforms", title: "Which platforms do you use?", type: "multi", opts: PLANNER_STEPS.platforms },
    { key: "start_time", title: "When do you want to start?", type: "single", opts: PLANNER_STEPS.timelines },
    { key: "contact", title: "Almost there — how do we reach you?", type: "contact" },
  ];
  const cur = steps[step];
  const isLast = step === steps.length - 1;

  const canNext = () => {
    if (cur.type === "single") return !!data[cur.key];
    if (cur.type === "multi") return data[cur.key].length > 0;
    if (cur.type === "contact") return data.name.trim() && /^\d{7,15}$/.test(data.phone.replace(/\s|-/g, ""));
    return true;
  };

  const next = async () => {
    if (!canNext()) { toast.error("Please complete this step to continue."); return; }
    if (!isLast) { setStep((s) => s + 1); return; }
    setLoading(true);
    track("planner_submit", { budget: data.budget, objective: data.objective });
    try {
      const res = await submitPlanner(data);
      setResult(res.plan);
      track("planner_success", {});
    } catch (e) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setStep(0); setData(initial); };

  const waSummary = result
    ? `Hi PixelPapad! From your AI planner:%0A- Business: ${data.business_type}%0A- Objective: ${data.objective}%0A- Budget: ${data.budget}%0A- Recommended: ${result.recommended_plan}`
    : "";

  return (
    <section id="planner" className="relative py-24 md:py-32 bg-[#050507]" data-testid="planner">
      <div className="pp-container">
        <SectionHeading overline="AI Project Planner" title="Tell Us What You Want to Build"
          sub="Answer a few quick questions and our AI strategist will draft an indicative plan in seconds." align="center" testid="planner-heading" />

        <div className="max-w-3xl mx-auto pp-glass rounded-2xl p-6 md:p-10 min-h-[420px] flex flex-col">
          {!result ? (
            <>
              {/* progress */}
              <div className="flex items-center gap-1.5 mb-8">
                {steps.map((_, k) => (
                  <div key={k} className={`h-1 flex-1 rounded-full transition-colors ${k <= step ? "bg-[#f59e0b]" : "bg-white/10"}`} />
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={step}
                  initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }} className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Step {step + 1} of {steps.length}</p>
                  <h3 className="font-display uppercase text-2xl md:text-3xl text-white mb-6">{cur.title}</h3>

                  {cur.type !== "contact" ? (
                    <div className="flex flex-wrap gap-2.5">
                      {cur.opts.map((o) => {
                        const active = cur.type === "multi" ? data[cur.key].includes(o) : data[cur.key] === o;
                        return (
                          <button key={o} data-testid={`planner-opt-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                            onClick={() => (cur.type === "multi" ? toggle(cur.key, o) : set(cur.key, o))}
                            className={CHIP(active)}>
                            {cur.type === "multi" && active && <Check size={14} className="inline mr-1" />}{o}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input data-testid="planner-name" placeholder="Your name *" value={data.name} onChange={(e) => set("name", e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                      <input data-testid="planner-phone" placeholder="Phone number *" value={data.phone} onChange={(e) => set("phone", e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                      <input data-testid="planner-email" placeholder="Email" value={data.email} onChange={(e) => set("email", e.target.value)}
                        className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                      <textarea data-testid="planner-details" placeholder="Add project details (optional)" value={data.details} onChange={(e) => set("details", e.target.value)} rows={3}
                        className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none resize-none" />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                  data-testid="planner-back"
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <button onClick={next} disabled={loading} data-testid="planner-next"
                  className="inline-flex items-center gap-2 bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-xs px-7 py-3.5 hover:bg-white transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : isLast ? <><Sparkles size={16} /> Generate My Plan</> : <>Continue <ArrowRight size={16} /></>}
                </button>
              </div>
            </>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="planner-result" className="flex-1">
              <div className="flex items-center gap-2 mb-4"><Sparkles className="text-[#f59e0b]" size={20} /><span className="pp-overline">Your Recommended Plan</span></div>
              <h3 className="font-display uppercase text-3xl md:text-4xl pp-text-gradient">{result.recommended_plan}</h3>
              <p className="mt-4 text-slate-300 leading-relaxed">{result.rationale}</p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="pp-overline mb-2">Recommended Services</p>
                  <div className="flex flex-wrap gap-2">
                    {(result.recommended_services || []).map((s) => (
                      <span key={s} className="px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 text-slate-200">{s}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="pp-overline mb-2">Indicative Budget</p>
                  <p className="font-display text-2xl text-[#f59e0b]">{result.budget_range}</p>
                  <p className="text-xs text-slate-500 mt-1">{result.next_step}</p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3 pt-6 border-t border-white/10">
                <a href={`${waLink().split("?")[0]}?text=${waSummary}`} target="_blank" rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { location: "planner_result" })}
                  data-testid="planner-whatsapp"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-black font-semibold uppercase tracking-widest text-xs px-6 py-3.5 hover:opacity-90 transition-opacity">
                  <MessageCircle size={16} /> Send on WhatsApp
                </a>
                <button onClick={reset} data-testid="planner-reset"
                  className="inline-flex items-center gap-2 border border-white/15 text-white font-semibold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-white/5 transition-colors">
                  <RotateCcw size={16} /> Start Over
                </button>
              </div>
              <p className="mt-4 text-xs text-slate-500">This is an indicative recommendation. Final pricing is confirmed after a discovery call.</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
